import { supabase } from '../lib/supabase'

/**
 * All access to real shoot data goes through these token-checked database
 * functions — never a direct table read. The token in the URL is the only
 * credential; there is no login for crew or clients on this app.
 */

export interface RemoteScheduleItem {
  id: string
  timeLabel: string
  endTimeLabel: string | null
  title: string
  durationNote: string | null
  peopleNote: string | null
  locationNote: string | null
  note: string | null
}

export interface RemoteCrewMember {
  name: string
  role: string | null
  phone: string | null
}

/** An extra client contact, featured guest, or freelance/on-the-day crew
    member — additive to the project's main crew and single client contact. */
export interface RemoteContact {
  name: string
  role: string | null
  phone: string | null
  /** Only present on extraCrew entries. */
  remote?: boolean
}

export interface RemoteShotItem {
  id: string
  section: 'Essential' | 'B-Roll' | 'Optional'
  name: string
  cameraLabel: string | null
  referenceImageUrl: string | null
  checked: boolean
}

export interface RemoteKitItem {
  id: string
  section: string
  name: string
  quantity: string | null
  owner: string | null
  checked: boolean
}

export interface RemoteProductionFiles {
  scriptUrl: string | null
  guestBiosUrl: string | null
  storyboardUrl: string | null
  dropboxUrl: string | null
}

export interface RemoteProductionStaffOnly {
  /** Merged 8 Sep 2026 — Client Notes was folded into this field on the
      builder side; the database no longer returns a separate clientNotes.
      Not covered by the Files segment switch below: Important Instructions
      is core to a call sheet and always travels. */
  importantInstructions: string | null
  /** All four null when the Files segment is switched off for this shoot. */
  files: RemoteProductionFiles
}

/**
 * Which optional segments this shoot publishes (13 Sep 2026).
 *
 * Set per shoot in the Call Sheet Builder — "we have a live broadcast
 * tomorrow and the schedule is very important but kit and shot types is
 * not." Reported here for completeness, but nothing in this app needs to
 * read it: the database withholds a switched-off segment's rows rather than
 * sending them for the UI to hide, so a segment that's off simply arrives
 * as an empty list. See App.tsx for how the tabs follow from that.
 */
export interface RemoteSections {
  shots: boolean
  kit: boolean
  files: boolean
}

export interface RemoteShootDay {
  /**
   * Crew and staff were merged into one full-access tier 9 Sep 2026 — every
   * crew link now includes everything the old staff link did (Shots, Kit,
   * and the Production notes/files). 'client' is the new, lighter tier: no
   * Shots, no Kit, no Production — see productionStaffOnly below.
   */
  role: 'crew' | 'client'
  production: {
    clientName: string | null
    projectName: string | null
    location: string | null
    date: string | null
    /** Free-text venue address — visible to crew, shown on Today. */
    locationAddress: string | null
    /** A pasted Google Maps (or similar) link, used directly for "Open
        Location" when present instead of building a maps search query. */
    locationMapUrl: string | null
    /** Visible to crew, shown on Today under "On the ground". */
    parkingNotes: string | null
  }
  currentScheduleIndex: number
  /**
   * 9 Sep 2026: whether the schedule's "current" position should be
   * computed live from the clock ('auto', the default) or trusted exactly
   * as currentScheduleIndex says ('manual' — set by advanceSchedule, i.e.
   * whenever someone taps an item to make it current, including backward).
   * Flips back to 'auto' only via resumeAutoSchedule; the clock never
   * silently reclaims control on its own.
   */
  scheduleMode: 'auto' | 'manual'
  /** Reported for completeness — see RemoteSections. Already reflects the
      role, so a client link reads false for all three. */
  sections: RemoteSections
  schedule: RemoteScheduleItem[]
  crew: RemoteCrewMember[]
  extraCrew: RemoteContact[]
  clientContacts: RemoteContact[]
  guests: RemoteContact[]
  /** Empty for role 'client', and empty for a crew link whose Shots segment
      is switched off — the database itself withholds these, not just the UI. */
  shots: RemoteShotItem[]
  /** Empty for role 'client', and empty when the Kit segment is switched
      off — same as shots above. */
  kit: RemoteKitItem[]
  /** Populated only for role 'crew'; always null for role 'client'. */
  productionStaffOnly: RemoteProductionStaffOnly | null
}

/** The token this page was opened with — crew and client each get their own. */
export function getTokenFromUrl(): string {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('token') ?? ''
}

export async function fetchShootDay(token: string): Promise<RemoteShootDay | null> {
  if (!supabase || !token) return null
  const { data, error } = await supabase.rpc('get_shoot_day_by_token', { p_token: token })
  if (error) {
    console.warn(`[supabase] get_shoot_day_by_token failed: ${error.message}`)
    return null
  }
  return (data as RemoteShootDay | null) ?? null
}

export async function toggleShot(token: string, shotId: string, checked: boolean): Promise<void> {
  if (!supabase || !token) return
  const { error } = await supabase.rpc('toggle_shoot_day_shot', {
    p_token: token,
    p_shot_id: shotId,
    p_checked: checked,
  })
  if (error) console.warn(`[supabase] toggle_shoot_day_shot failed: ${error.message}`)
}

export async function toggleKitItem(token: string, itemId: string, checked: boolean): Promise<void> {
  if (!supabase || !token) return
  const { error } = await supabase.rpc('toggle_shoot_day_kit_item', {
    p_token: token,
    p_item_id: itemId,
    p_checked: checked,
  })
  if (error) console.warn(`[supabase] toggle_shoot_day_kit_item failed: ${error.message}`)
}

/** Sets the schedule's "current" item outright — any index, forward or
    backward — and switches the shoot into 'manual' mode server-side (see
    RemoteShootDay.scheduleMode) so the clock-driven auto-advance stops
    overriding it until resumeAutoSchedule is called. Used both for the
    quick "advance to next" action and for tapping any other item directly
    to correct the position when a shoot is running behind or ahead. */
export async function advanceSchedule(token: string, newIndex: number): Promise<void> {
  if (!supabase || !token) return
  const { error } = await supabase.rpc('advance_shoot_day_schedule', {
    p_token: token,
    p_new_index: newIndex,
  })
  if (error) console.warn(`[supabase] advance_shoot_day_schedule failed: ${error.message}`)
}

/** Hands control of "current" back to the clock (scheduleMode -> 'auto').
    The stored currentScheduleIndex is left as-is — it's simply ignored
    again from this point on, in favor of the live clock computation. */
export async function resumeAutoSchedule(token: string): Promise<void> {
  if (!supabase || !token) return
  const { error } = await supabase.rpc('resume_shoot_day_auto_schedule', { p_token: token })
  if (error) console.warn(`[supabase] resume_shoot_day_auto_schedule failed: ${error.message}`)
}
